import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Link } from "react-router-dom";
import type {
  LegacyCaseCollision,
  LegacyDocumentSummary,
} from "../../../types/legacyCases";
import { useAttachLegacyDocumentToCase } from "../../../hooks/useLegacyCaseCollision";
import { LEGACY_CASE_MODAL_Z_INDEX } from "./legacyCaseOverlay";

interface Props {
  collision: LegacyCaseCollision;
  documents: LegacyDocumentSummary[];
  disabled?: boolean;
}

export default function LegacyExistingCaseDocumentAttachment({
  collision,
  documents,
  disabled,
}: Props) {
  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState<LegacyCaseCollision["existingCase"]>();
  const [success, setSuccess] = useState("");
  const mutation = useAttachLegacyDocumentToCase(collision.archiveId);
  const validateDocument = useCallback(
    (value: string) =>
      documents.some((document) => document.id === value)
        ? null
        : "Select an archived document to attach.",
    [documents]
  );
  const validateConfirmation = useCallback(
    (value: boolean) =>
      value ? null : "Confirm the existing case and document before attaching.",
    []
  );
  const form = useForm<{ documentId: string; confirmed: boolean }>({
    initialValues: { documentId: "", confirmed: false },
    validate: {
      documentId: validateDocument,
      confirmed: validateConfirmation,
    },
  });
  const options = useMemo(
    () =>
      documents.map((document) => ({
        value: document.id,
        label: `${
          document.kind === "report" ? "Final report" : "Requisition"
        } · ${document.originalFileName}`,
      })),
    [documents]
  );
  const targetChanged = useMemo(
    () =>
      Boolean(
        target &&
          (target.id !== collision.existingCase?.id ||
            !collision.importBlocked ||
            !collision.canAttachDocuments)
      ),
    [collision, target]
  );
  const handleOpen = useCallback(() => {
    if (!collision.existingCase || !collision.canAttachDocuments) return;
    setTarget(collision.existingCase);
    form.reset();
    mutation.reset();
    setSuccess("");
    setOpened(true);
  }, [collision, form, mutation]);
  const handleClose = useCallback(() => {
    if (!mutation.isPending) setOpened(false);
  }, [mutation.isPending]);
  const handleDocument = useCallback(
    (value: string | null) => {
      form.setFieldValue("documentId", value || "");
      form.setFieldValue("confirmed", false);
    },
    [form]
  );
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (mutation.isPending || targetChanged || disabled || !target) return;
      const validation = form.validate();
      if (validation.hasErrors) {
        form.getInputNode(Object.keys(validation.errors)[0])?.focus();
        return;
      }
      try {
        const result = await mutation.mutateAsync({
          caseId: target.id,
          documentId: form.getValues().documentId,
          confirmed: true,
        });
        setSuccess(
          result.alreadyAttached
            ? "This document is already attached to the existing case."
            : "Document attached to the existing case. Your archive review is unchanged."
        );
        setOpened(false);
      } catch {
        /* The translated mutation error remains visible in the dialog. */
      }
    },
    [disabled, form, mutation, target, targetChanged]
  );
  if (!collision.canAttachDocuments && !opened) return null;
  return (
    <Stack gap="sm">
      <Button
        variant="light"
        onClick={handleOpen}
        disabled={disabled || !documents.length}
      >
        Attach an archive document to this case
      </Button>
      {success && (
        <Alert color="green" role="status">
          {success}
        </Alert>
      )}
      <Modal
        zIndex={LEGACY_CASE_MODAL_Z_INDEX}
        opened={opened}
        onClose={handleClose}
        title="Attach an archived document"
        centered
        closeOnClickOutside={false}
        closeOnEscape={!mutation.isPending}
        withCloseButton={!mutation.isPending}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            <Text>
              Destination:{" "}
              <Anchor
                component={Link}
                to={`/cases/${target?.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {target?.caseNumber}
              </Anchor>
            </Text>
            <Text size="sm">
              This adds the selected original file as an ordinary case document.
              It does not create a new case, change patient or case details, or
              replace the existing report. Your archive draft is retained.
            </Text>
            <Select
              label="Archived document"
              withAsterisk
              aria-required
              data={options}
              clearable
              {...form.getInputProps("documentId")}
              onChange={handleDocument}
              comboboxProps={{
                withinPortal: true,
                position: "bottom-start",
                offset: 8,
              }}
            />
            <Checkbox
              label="I checked the existing case and want to attach this document"
              {...form.getInputProps("confirmed", { type: "checkbox" })}
            />
            {targetChanged && (
              <Alert color="orange" title="Destination changed">
                Close this dialog, refresh the case check, and review the
                destination before attaching.
              </Alert>
            )}
            {mutation.error && (
              <Alert color="red" title="Document not attached">
                {mutation.error.message}
              </Alert>
            )}
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={mutation.isPending}
                disabled={targetChanged || disabled}
              >
                Attach document
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
